import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import cx from "classnames";
import { Offcanvas, Row, Col, Form, Modal, Button, ButtonGroup, Image, Tooltip, OverlayTrigger, Popover ,Dropdown } from 'react-bootstrap';

import styles from './versionHistory.module.scss'
import { useSelector, useDispatch } from 'react-redux';
import { deleteVersion, fetchVersions, renameVersion, restoreVersion, invalidateRestoreState} from 'actions/versions';
import { useParams, useHistory } from 'react-router-dom';
import dateformat from "dateformat";
import { fetchPage } from 'actions/pages';

import { toast } from "react-toastify";
import { SuccessToast } from "components/Toast";

export function VersionHistory({ device, handleDeviceLayout }) {

  const dispatch = useDispatch();
  const history = useHistory();

  const { id, versionId } = useParams();
  const page = useSelector((state) => _.get(state, `pages.page.${id}.item`));

  const storyboard = _.get(page, 'storyboard', null);
  const versions = useSelector(state => _.get(state, 'versions.items', []));
  const versionsSortedByLatest = [...versions].sort((a, b) => {
    const dateA = new Date(a.createdDate);
    const dateB = new Date(b.createdDate);
    return dateB - dateA;
  });

  const versionHistory = [{ id: "current", name: "Current Version" }, ...versionsSortedByLatest];
  const versionHistoryContainer = useRef(null);

  const [state, setState]= useState({
    showVersionHistory: true,
    showRestoreModal: false,
    showDeleteModal: false,
    selectedVersion: {}
  })

  const [icons, setIcons] = useState({
    primary: true,
    secondary: true
  })

  const handleVersionChange = (version) => {
    const versionId = _.get(version, 'id');
    // Storyboard info is not available from the store during route changes, so query params are used.
    history.push(`${versionId}?storyboardId=${storyboard.id}&storyboardType=${storyboard.type}`)
  }

  const updateProperty = (fieldName, value) => {
    document.body.click();

    setState((state)=>{
      return{
        ...state,
        [fieldName]:value
      }
    })
  }

  const handleRestore = () => {
    let versionId = _.get(state, 'selectedVersion.id');
    let versionName = _.get(state, 'selectedVersion.name');

    dispatch(restoreVersion(versionId, (pageResponse)=>{
      toast(
        <SuccessToast
          heading={`Success`}
          description={`${versionName} restored successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      if(pageResponse){
        const targetWindow = window.opener;
        targetWindow.postMessage({
          type: "restorePage",
          payload: {
            id: pageResponse.id
          }
        }, '*');
      }
      dispatch(invalidateRestoreState());
    }));
    updateProperty('showRestoreModal', false);
  }

  const handleDelete = () => {
    let versionId = _.get(state, 'selectedVersion.id');
    let versionName = _.get(state, 'selectedVersion.name');

    dispatch(deleteVersion(versionId, ()=>{
      history.push('current');
      versionHistoryContainer.current.scrollTop = 0;

      toast(
        <SuccessToast
          heading={`Success`}
          description={`${versionName} deleted`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );
    }));
    updateProperty('showDeleteModal', false);
  }

  useEffect(()=>{
    if(versionId !== 'current'){
      const version = versions.find(item => item.id == versionId);
      updateProperty('selectedVersion', version);
      return
    }

    updateProperty('selectedVersion', versionHistory[0]);
  },[versionId, versions])

  useEffect(()=>{
    if(storyboard){
      dispatch(fetchVersions(storyboard.id))
    }
  },[storyboard])

  useEffect(()=>{
    if(device == 'none' || device == 'tabletls') {
      setIcons({
          primary: device === 'none',
          secondary: true
        })
      return;
    }
    setIcons({
        primary: true,
        secondary: device === 'mobile' 
      })
  },[device])

  useEffect(() => {
    dispatch(fetchPage(id));
  }, [id]);

  return (
    <div className={styles.versionHistory}>
      <div className={styles.actionBtnContainer}>
          <OverlayTrigger trigger="focus hover" placement={'bottom'} overlay={<Tooltip>Version History</Tooltip>}>
            <Button className={styles.actionBtn} onClick={()=>updateProperty('showVersionHistory', true)}>
                <i className={cx("cleverstory-clock")} />
            </Button>
          </OverlayTrigger>
      </div>
      <Offcanvas className={styles.historyPanel} show={state.showVersionHistory} onHide={()=>updateProperty('showVersionHistory', false)} placement='end' backdrop={false}> 
        <Offcanvas.Header className={styles.header}>
            <h4 className={styles.title}>Version History </h4>
            <p className={styles.description}> A record of your Content’s various phases.</p>
            <button className={styles.closeBtn} onClick={()=>updateProperty('showVersionHistory', false)}>
                <i className={cx('cleverstory-drop-down')} />
            </button>
        </Offcanvas.Header>
        <Offcanvas.Body className={styles.history} ref={versionHistoryContainer}>
          { versionHistory.map(( version, index )=> {
              return(
                <VersionDetails 
                key={version.id} 
                version={version} 
                updateProperty={updateProperty}
                versionId={versionId}
                handleVersionChange={handleVersionChange}
              />
              )
          })}
        </Offcanvas.Body>
        <footer className={styles.footer}>
          <OverlayTrigger placement={'top'} overlay={<Tooltip>Only files that exist in the media library will be restored.</Tooltip>}>
            <span className={styles.warningMsg}>
                <i className={cx("cleverstory-warning")} /> 
                <span>Can’t Restore Media</span>
            </span>
          </OverlayTrigger>

          <ButtonGroup className={styles.devicesSwitcher}>
              <span className={styles.layoutOptions}>
                <Button className={cx({ [styles.active]: (device == 'none' || device == 'tabletls') })} 
                  onClick={()=>handleDeviceLayout(device === 'none' ? 'tabletls' : 'none')}
                  onMouseEnter={()=> {
                    if(device == 'none' || device == 'tabletls'){
                    setIcons((state)=>{
                    return {
                      ...state,
                      primary: !state.primary 
                    }})
                  }}}
                  onMouseLeave={()=> {
                    if(device == 'none' || device == 'tabletls'){
                      setIcons((state)=>{
                      return {
                        ...state,
                        primary: device == 'none' 
                      }})
                    }}}
                  >
                  {icons.primary  && <i className={cx("cleverstory-laptop", styles.icon)} />}
                  {!icons.primary  && <i className={cx("cleverstory-tablet-ls", styles.icon)} />}
                </Button>

                <Button className={cx({ [styles.active]: (device == 'mobile' || device == 'tablet') })} 
                  onClick={()=>handleDeviceLayout(device === 'mobile' ? 'tablet' : 'mobile')}
                  onMouseEnter={()=> {
                    if(device == 'mobile' || device == 'tablet'){
                    setIcons((state)=>{
                    return {
                      ...state,
                      secondary: !state.secondary 
                    }})
                  }}}
                  onMouseLeave={()=> {
                    if(device == 'mobile' || device == 'tablet'){
                      setIcons((state)=>{
                      return {
                        ...state,
                        secondary: device == 'mobile' 
                    }})
                  }}}
                >
                  {icons.secondary && <i className={cx("cleverstory-mobile", styles.icon)} /> }
                  {!icons.secondary &&  <span className={styles.rotate}><i className={cx("cleverstory-tablet-ls", styles.icon)}/> </span>}
                </Button>
              </span>
          </ButtonGroup>
        </footer>
      </Offcanvas>

      <VersionModal 
        type="restore" 
        show={state.showRestoreModal} 
        handleRestore={handleRestore}
        onDiscard={()=>updateProperty('showRestoreModal', false)} 
        heading={'Planning to time travel to a previous version? '} 
        description={`Before restoring, make sure all changes are saved and the page is not being edited.`} />

      <VersionModal 
        type="delete" 
        show={state.showDeleteModal} 
        handleDelete={handleDelete}
        onDiscard={()=>updateProperty('showDeleteModal', false)} 
        heading={'Wipe this version from memory?'} 
        description={`This iteration of content can no longer be accessed or restored.`} />
    </div>
  )
}

const VersionDetails = ({ version, updateProperty, versionId, handleVersionChange }) => {

  const dispatch = useDispatch();

  const inputRef = React.useRef(null);

  const versionName = _.get(version, 'name');
  const firstName = _.get(version, 'createdBy.firstName');
  const lastName = _.get(version, 'createdBy.lastName');
  const createdDate = _.get(version, 'createdDate');

  const [state, setState] = useState({
    rename: false,
    name: versionName
  });

  const getFormattedDate = (dateString) => {
    return dateformat(dateString, `mmm dd, yyyy, hh:MM TT`)
  }

  const toggleEditClick = () => {
    document.body.click();

    if(inputRef.current){
      let request = {
        name: _.get(inputRef, 'current.value'),
        snapshot: _.get(version, 'id')
      }
      dispatch(renameVersion(request));
    }

    setState(state => {
      return {
        ...state,
        rename: !state.rename
      }
    })
  }

  useEffect(()=>{
    if(state.rename){
      inputRef.current.focus();
    }
  },[state.rename])

  const handleNameChange = (e) => {
    setState(state => {
      return {
        ...state,
        name: e.target.value
      }
    })
  }

  const popover = (
    <Popover key={version.id} bsPrefix="popover" className={styles.versionSettingsPopover}>
      <Popover.Body className={styles.settingsPopoverBody}>
        <div className={styles.settingsPopoverContent}>
          <span onClick={toggleEditClick}>Rename</span>
          <span onClick={()=>updateProperty('showRestoreModal', true)}>Restore</span>
          <span onClick={()=>updateProperty('showDeleteModal', true)}>Delete</span>
        </div>
      </Popover.Body>
    </Popover>
  );

  return(
    <div className={cx(styles.version, {[styles.selected]:version.id === versionId})} onClick={()=>handleVersionChange(version)}>
      <div className={styles.lineContainer}>
        <div className={cx(styles.connectingLine, styles.top)}></div>
        <div className={styles.dot}></div>
        <div className={cx(styles.connectingLine, styles.bottom)}></div>
      </div>
      <div className={styles.details}>
        {!state.rename && <div className={styles.name} >{versionName}</div> }
        {(version.id === versionId) && state.rename &&
          <Form.Control className={styles.nameInput} type="text" ref={inputRef} onChange={handleNameChange} value={state.name} placeholder="Enter version name" onBlur={toggleEditClick} />
        }
        {version.id !== "current" &&
          <>
            <div className={styles.timeline}>
              <span className={styles.date}>{getFormattedDate(createdDate)}</span>
              {version.published &&
                <label className={cx(styles.status, {[styles.live]:version.status === "live", [styles.published]:version.published})}>
                  PUBLISHED
                </label>
              }
            </div>
            <div className={styles.user}><i className={cx('cleverstory-user')} /><span>{`${firstName} ${lastName}`}</span></div>
          </>
        }
      </div>
      {version.id !== "current" &&
        <OverlayTrigger key={version.id} rootClose={true}  trigger="click" placement='bottom' overlay={popover}>
          <Button variant="info" className={styles.moreButton}>
            <i className={cx('cleverstory-more')} />
          </Button>
        </OverlayTrigger>
      }
    </div>
  )
}

const VersionModal = ({type, show, onDiscard, heading, description, handleRestore, handleDelete }) => {

  return (
    <Modal backdrop='static'
      className={styles.versionModal}
      contentClassName={styles.versionContent}
      dialogClassName={styles.versionDialog}
      backdropClassName={styles.backdropWrapper}
      show={show}
      centered size="lg"
    >
      <button className={styles.closeBtn} onClick={onDiscard}>
          <i className={cx('cleverstory-close')} />
      </button>
      <Modal.Body className={styles.versionBody}>
        <Row className={styles.messageRow}>
          <Col className={styles.messageColumn} xs={6}>
            <div>
              <h2 className={styles.title}>{heading}</h2>
              <p className={styles.description}>{description}</p>
            </div>
            <footer className={styles.saveChangeButtons}>
              {type === "restore" && <Button variant="primary" onClick={handleRestore}>Restore Version</Button>}
              {type === "delete" && <Button variant="primary" onClick={handleDelete}>Delete Version</Button>}
              <Button variant="secondary" onClick={onDiscard}>Cancel</Button>
            </footer>
          </Col>
          <Col className={styles.imageColumn} xs={6}>
            <Image src={'/images/versioning.png'} className={styles.versionImage} />
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  )
}